import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Dialog,
  Grid,
  makeStyles,
  Typography,
} from "@material-ui/core";

import { Loading, SelectField } from "react-admin";

import { isAuthorized } from "../../../../utils/appUtils";
import { AclType, ShipmentLineItemStatuses } from "../../../../utils/constants";
import { getPhoto, getPhotoUrl } from "../../../../utils/fileUtils";
import { exceptionRenderer } from "../../../../utils/utils";

import { accountNameRenderer } from "../../../../components/common/AccountNameRenderer";
import { CopyToClipboardButton } from "../../../../components/common/CopyToClipboardButton";

const useAsideStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.up("sm")]: {
      width: "18em",
      minWidth: "18em",
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(0.5),
      overflow: "initial",
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  exception: {
    marginTop: theme.spacing(0.5),
  },
}));

export const PickListItemAside = ({ backPath, record, ...props }) => {
  const classes = useAsideStyles();
  const imgRef = useRef();
  const [photo, setPhoto] = useState();
  const [selectedPhoto, setSelectedPhoto] = useState();
  const [isOpen, setIsOpen] = useState(false);
  const { hasException, barcodeException } = useMemo(() => {
    let hasExceptionTemp = false;
    let barcodeExceptionTemp = false;

    record?.inventoryItems?.items?.forEach((item) => {
      if (item.hasException) hasExceptionTemp = true;
      if (item.barcodeException) barcodeExceptionTemp = true;
      if (hasExceptionTemp && barcodeExceptionTemp) return;
    });

    return {
      hasException: hasExceptionTemp,
      barcodeException: barcodeExceptionTemp,
    };
  }, [record?.inventoryItems?.items]);

  const exceptions = { hasException, barcodeException };

  const linkBackPath = backPath
    ? [...backPath, props.location.pathname]
    : [props.location.pathname];

  useEffect(() => {
    if (record?.pickupPhoto && imgRef.current) {
      setSelectedPhoto(record.pickupPhoto.key);
      getPhoto(record.pickupPhoto.key, setPhoto, imgRef.current.clientWidth);
    }
  }, [imgRef, record]);

  const handleMagnifyPhoto = () => {
    setIsOpen(!isOpen);
  };

  if (!record) return <Loading />;
  return (
    <Card className={classes.root} ref={imgRef}>
      <CardContent>
        <Grid container direction="column" spacing={1}>
          <Grid item xs={12}>
            <Typography variant="overline" color="textSecondary">
              Status:{" "}
            </Typography>
            <br />
            <SelectField
              source="status"
              record={record}
              choices={ShipmentLineItemStatuses}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="overline" color="textSecondary">
              Account:{" "}
            </Typography>
            <br />
            {record.inventory?.accountId ? (
              <Link
                to={{
                  pathname: isAuthorized(AclType.ACCOUNTS)
                    ? `/accounts/${record.inventory.accountId}`
                    : `/accounts/${record.inventory.accountId}/show`,
                  state: { backPath: linkBackPath },
                }}
              >
                <Typography variant="body2" color="primary">
                  {accountNameRenderer(record.inventory.account)}
                </Typography>
              </Link>
            ) : (
              // No inventory on the line item (deleted inventory, or the
              // nested field filtered out by auth) — nothing to link to.
              <Typography variant="body2">
                {accountNameRenderer(record.inventory?.account) || "—"}
              </Typography>
            )}
          </Grid>
          {exceptions.hasException && (
            <Grid item xs={12}>
              <Typography variant="overline" color="textSecondary">
                Exceptions
              </Typography>
            </Grid>
          )}
          {exceptions.hasException && exceptions.barcodeException && (
            <Grid container direction="row">
              <Grid item xs={2}>
                {exceptionRenderer(exceptions, "left")}
              </Grid>
              <Grid item xs={10} className={classes.exception}>
                <Typography variant="body2">Barcode Exception</Typography>
              </Grid>
            </Grid>
          )}
          {record.inventory?.id && (
            <Grid item xs={12}>
              <Typography variant="overline" color="textSecondary">
                Inventory:{" "}
              </Typography>
              <br />
              <Link
                to={{
                  pathname: `/inventories/${record.inventory.id}`,
                  state: { backPath: linkBackPath },
                }}
              >
                <Typography variant="body2" color="primary">
                  {record.inventory.productName}
                </Typography>
              </Link>
            </Grid>
          )}
          {record.inventory?.productId && (
            <Grid item xs={12}>
              <Typography variant="overline" color="textSecondary">
                Product:
              </Typography>
              <br />
              <Box display="flex" alignItems="center">
                <Link
                  to={{
                    pathname: `/products/${record.inventory.productId}`,
                    state: {
                      account: record.inventory.account,
                      backPath: linkBackPath,
                    },
                  }}
                  style={{ textDecoration: "none" }}
                >
                  <Typography variant="body2" color="primary">
                    {record.inventory.productCode}
                  </Typography>
                </Link>
                <Box ml={1}>
                  <CopyToClipboardButton text={record.inventory.productCode} />
                </Box>
              </Box>
            </Grid>
          )}
          {record.pickupPhoto && (
            <Grid item xs={12}>
              <Typography variant="overline" color="textSecondary">
                <strong>Photos:</strong>
              </Typography>
              <br />
              <CardMedia
                component="img"
                image={photo}
                className={classes.media}
                onClick={handleMagnifyPhoto}
              />
            </Grid>
          )}
        </Grid>
      </CardContent>
      {isOpen && (
        <Dialog open={isOpen} onClick={handleMagnifyPhoto} maxWidth="xl">
          <img
            src={getPhotoUrl(selectedPhoto, window.innerWidth)}
            alt="Outbound Inventory"
            className={classes.mediaFull}
            onClick={handleMagnifyPhoto}
          />
        </Dialog>
      )}
    </Card>
  );
};
